import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import { Observable } from "rxjs/Observable";
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import {environment} from "../../environments/environment";
import { JobList, JobsListV2Item, JobsListV2Response, Job, Mechanic, NoteListEntity, TaskNote, TaskStatusUpdateResponse, Vehicle} from '../models/interfaces'

export interface JobSummary {
  todo: number;
  inProgress: number;
  waiting: number;
  done: number;
}

export interface UpcomingReminder {
  jobId: number;
  customerName: string;
  vehicleNumber: string;
  dropOffTime: string;
  daysUntil: number;
}

export interface TodayJob {
  jobId: number;
  jobCode: string;
  customerName: string;
  vehicleNumber: string;
  dropOffTime: string;
  statusId: number;
  statusDesc: string;
  mechanicName: string;
}

export interface WaitingJob {
  jobId: number;
  jobCode: string;
  customerName: string;
  vehicleNumber: string;
  dropOffTime: string;
  mechanicName: string;
  waitingHours: number;
}


@Injectable({
  providedIn: 'root'
})
export class JobService {

constructor(private http: HttpClient ) { }

  public searchJobs(
    searchTerm: string,
    page: number,
    pageSize: number,
    startDate?: string,
    endDate?: string,
    sortColumn: string = 'jobId',
    sortDirection: string = 'DESC'
  ): Observable<JobList> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(pageSize))
      .set('searchTerm', searchTerm == null ? '' : searchTerm)
      .set('sortColumn', sortColumn)
      .set('sortDirection', sortDirection);

    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get<JobsListV2Response>(environment.APIENDPOINT + '/v2/jobs/list', {
      headers: this.setHeaders(),
      params,
      responseType: 'json'
    }).pipe(
      map(response => this.mapJobsListResponse(response))
    );
  }

  private mapJobsListResponse(response: JobsListV2Response): JobList {
    const responseJobs = response && Array.isArray(response.jobList) ? response.jobList : [];

    return {
      noOfJobs: response && typeof response.noOfJobs === 'number' ? response.noOfJobs : 0,
      jobList: responseJobs.map(job => this.mapJobsListItem(job))
    };
  }

  private mapJobsListItem(item: JobsListV2Item): Job {
    const vehicle = item && item.vehicle;
    const customer = item && item.customer;
    const status = item && item.status;

    return {
      jobId: item && item.jobId != null ? item.jobId : undefined,
      jobCode: item && item.jobCode != null ? item.jobCode : '',
      jobType: item && item.jobType != null ? item.jobType : '',
      dropOffTime: item && item.dropOffTime != null ? item.dropOffTime : '',
      pickUpTime: item && item.pickUpTime != null ? item.pickUpTime : '',
      vehicle: {
        vehicleNumber: vehicle && vehicle.vehicleNumber != null ? vehicle.vehicleNumber : '',
        vin: vehicle && vehicle.vin != null ? vehicle.vin : ''
      } as Vehicle,
      customer: {
        firstName: customer && customer.firstName != null ? customer.firstName : '',
        lastName: customer && customer.lastName != null ? customer.lastName : '',
        contactNumber: customer && customer.contactNumber != null ? customer.contactNumber : ''
      },
      status: {
        id: status && status.id != null ? status.id : 0,
        code: status && status.code != null ? status.code : '',
        description: status && status.description != null ? status.description : ''
      }
    };
  }

  public getJobByID(jobID : string ): Observable<Job> {
    return this.http.get<Job>(environment.APIENDPOINT + '/jobs/' + jobID, {
      headers: this.setHeaders(),
      responseType: 'json'
    }).pipe(map(job => this.attachTaskMechanics(job)));
  }

  private attachTaskMechanics(job: Job): Job {
    const mechanics = (job && job.mechanics) || [];
    const taskList = (job && job.taskList) || [];
    taskList.forEach(task => {
      if (task.mechanic == null && task.mechanicId != null) {
        const mechanic = mechanics.find(m => m.id === task.mechanicId);
        if (mechanic != null) {
          task.mechanic = mechanic;
        }
      }
    });
    return job;
  }

  public getAllMechanics(): Observable<Mechanic[]> {
    return this.http.get<Mechanic[]>(environment.APIENDPOINT + '/mechanics', {
      headers: this.setHeaders(),
      responseType: 'json'
    });
  }
  

  public createOrUpdateJob( job : Job){

    return this.http.post(environment.APIENDPOINT + '/jobs', job, 
    {
        headers: this.setHeaders(),
        responseType: 'json'
    }
    );
  
  }

  public makeAsDeleteTheJob(jobID : number){

    return this.http.delete(environment.APIENDPOINT + '/jobs/'+jobID, 
    {
        headers: this.setHeaders(),
        responseType: 'json'
    }
    );
  
  }

  
  updateTaskStatus(jobId: number, taskId: number, status: number): Observable<TaskStatusUpdateResponse> {

    return this.http.post<TaskStatusUpdateResponse>(environment.APIENDPOINT + '/jobs/' + jobId + '/tasks/' + taskId + '/status', status, 
    {
        headers: this.setHeaders(),
        responseType: 'json'
    }
    );
  }


  updateNote(jobId: number, taskId: number, noteId: number, note: NoteListEntity){
    return this.http.post(environment.APIENDPOINT +  '/jobs/'+ jobId +'/tasks/' + taskId + '/notes/' + noteId, note, 
    {
        headers: this.setHeaders(),
        responseType: 'json'
    }
    );
  }

  saveNote(jobId: number, taskId: number, note: TaskNote){

    let URLpath ="";

    if(taskId==-1){ //job note
      URLpath =environment.APIENDPOINT +  '/jobs/'+ jobId +'/notes';
    }else{ // task note
      URLpath = environment.APIENDPOINT +  '/jobs/'+ jobId +'/tasks/' + taskId + '/notes';
    }

    return this.http.post(URLpath, note, 
    {
        headers: this.setHeaders(),
        responseType: 'json'
    }
    );


}



  public getUpcomingReminders(days: number = 7): Observable<UpcomingReminder[]> {
    const today = new Date();
    const end = new Date();
    end.setDate(today.getDate() + days);

    const fmt = (d: Date) => d.toISOString().split('T')[0];

    return this.http.get<Job[]>(
      `${environment.APIENDPOINT}/jobs/calendar?startDate=${fmt(today)}&endDate=${fmt(end)}`,
      { headers: this.setHeaders(), responseType: 'json' }
    ).pipe(
      map(jobs => jobs.map(job => {
        const dropOff = new Date(job.dropOffTime);
        const diffMs = dropOff.getTime() - new Date().setHours(0, 0, 0, 0);
        const daysUntil = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
        const firstName = job.customer?.firstName || '';
        const lastName  = job.customer?.lastName  || '';
        return {
          jobId:        job.jobId,
          customerName: (firstName + ' ' + lastName).trim() || 'Unknown',
          vehicleNumber: job.vehicle?.vehicleNumber || '',
          dropOffTime:  job.dropOffTime,
          daysUntil
        } as UpcomingReminder;
      }))
    );
  }

  public getJobSummary(): Observable<JobSummary> {
    const byStatus = (statuses: string) =>
      this.http.get<JobList>(environment.APIENDPOINT + '/jobs?statuses=' + statuses + '&page=0&size=1', {
        headers: this.setHeaders(),
        responseType: 'json'
      });

    return forkJoin({
      todo:       byStatus('1,2'),
      inProgress: byStatus('3,4'),
      waiting:    byStatus('5'),
      done:       byStatus('6')
    }).pipe(
      map(res => ({
        todo:       res.todo.noOfJobs || 0,
        inProgress: res.inProgress.noOfJobs || 0,
        waiting:    res.waiting.noOfJobs || 0,
        done:       res.done.noOfJobs || 0
      }))
    );
  }

  public getTodayJobs(): Observable<TodayJob[]> {
    const today = new Date().toISOString().split('T')[0];
    return this.http.get<Job[]>(
      `${environment.APIENDPOINT}/jobs/calendar?startDate=${today}&endDate=${today}`,
      { headers: this.setHeaders(), responseType: 'json' }
    ).pipe(
      map(jobs => jobs.map(job => {
        const firstName = job.customer?.firstName || '';
        const lastName  = job.customer?.lastName  || '';
        const mFirst    = (job.taskList || []).map(t => t.mechanic?.firstName).find(n => !!n) || '';
        const mLast     = (job.taskList || []).map(t => t.mechanic?.lastName).find(n => !!n) || '';
        return {
          jobId:        job.jobId,
          jobCode:      job.jobCode || '',
          customerName: (firstName + ' ' + lastName).trim() || 'Unknown',
          vehicleNumber: job.vehicle?.vehicleNumber || '',
          dropOffTime:  job.dropOffTime || '',
          statusId:     job.status?.id || 0,
          statusDesc:   job.status?.description || '',
          mechanicName: (mFirst + ' ' + mLast).trim() || '—'
        } as TodayJob;
      }))
    );
  }

  public getWaitingJobs(): Observable<WaitingJob[]> {
    return this.http.get<JobList>(
      `${environment.APIENDPOINT}/jobs?statuses=5&page=0&size=10`,
      { headers: this.setHeaders(), responseType: 'json' }
    ).pipe(
      map(res => {
        const jobs = res.jobList || [];
        const now = new Date().getTime();
        return jobs.map(job => {
          const firstName = job.customer?.firstName || '';
          const lastName  = job.customer?.lastName  || '';
          const mFirst    = (job.taskList || []).map(t => t.mechanic?.firstName).find(n => !!n) || '';
          const mLast     = (job.taskList || []).map(t => t.mechanic?.lastName).find(n => !!n) || '';
          const dropMs    = job.dropOffTime ? new Date(job.dropOffTime).getTime() : now;
          const waitHours = Math.round((now - dropMs) / (1000 * 60 * 60));
          return {
            jobId:        job.jobId,
            jobCode:      job.jobCode || '',
            customerName: (firstName + ' ' + lastName).trim() || 'Unknown',
            vehicleNumber: job.vehicle?.vehicleNumber || '',
            dropOffTime:  job.dropOffTime || '',
            mechanicName: (mFirst + ' ' + mLast).trim() || 'Unassigned',
            waitingHours: Math.max(0, waitHours)
          } as WaitingJob;
        });
      })
    );
  }

  private setHeaders() {
    let currentUser = JSON.parse(localStorage.getItem('currentUser'));
    let userTenant = localStorage.getItem('userTenant');
    let authHeader = new HttpHeaders({'Authorization': 'Bearer ' + currentUser.token , 'X-Tenant' : userTenant });
    return authHeader;
  }

  sendReviewReminder(jobId: number | string): Observable<any> {
    return this.http.post<any>(
      environment.APIENDPOINT + '/jobs/' + jobId + '/sendreviewreminder',
      {},
      { headers: this.setHeaders() }
    );
  }

  getReviewByJob(jobId: number | string): Observable<any> {
    return this.http.get<any>(
      environment.APIENDPOINT + '/jobs/' + jobId + '/review',
      { headers: this.setHeaders() }
    );
  }

  getReviewsByCustomer(customerId: number | string, page: number = 0, size: number = 10): Observable<any> {
    return this.http.get<any>(
      `${environment.APIENDPOINT}/jobs/customer/${customerId}/reviews?page=${page}&size=${size}`,
      { headers: this.setHeaders() }
    );
  }

  // --- Job Bundles (Kits) API Methods ---

  public getJobBundles(jobId: number | string): Observable<any[]> {
    return this.http.get<any[]>(`${environment.APIENDPOINT}/v2/jobs/${jobId}/bundles`, {
      headers: this.setHeaders(),
      responseType: 'json'
    });
  }

  public getJobBundleById(jobId: number | string, bundleId: number | string): Observable<any> {
    return this.http.get<any>(`${environment.APIENDPOINT}/v2/jobs/${jobId}/bundles/${bundleId}`, {
      headers: this.setHeaders(),
      responseType: 'json'
    });
  }

  public applyBundleToJob(jobId: number | string, bundleId: number | string): Observable<any> {
    return this.http.post<any>(`${environment.APIENDPOINT}/v2/jobs/${jobId}/bundles`, { bundleId: Number(bundleId) }, {
      headers: this.setHeaders(),
      responseType: 'json'
    });
  }

  public editJobBundleItems(jobId: number | string, bundleId: number | string, items: any[]): Observable<any> {
    return this.http.put<any>(`${environment.APIENDPOINT}/v2/jobs/${jobId}/bundles/${bundleId}/items`, items, {
      headers: this.setHeaders(),
      responseType: 'json'
    });
  }

  public removeJobBundle(jobId: number | string, bundleId: number | string): Observable<any> {
    return this.http.delete<any>(`${environment.APIENDPOINT}/v2/jobs/${jobId}/bundles/${bundleId}`, {
      headers: this.setHeaders(),
      responseType: 'json'
    });
  }

  public consumeJobBundle(jobId: number | string, bundleId: number | string): Observable<any> {
    return this.http.post<any>(`${environment.APIENDPOINT}/v2/jobs/${jobId}/bundles/${bundleId}/consume`, {}, {
      headers: this.setHeaders(),
      responseType: 'json'
    });
  }

  public createCustomJobBundle(jobId: number | string, items: any[], name: string = 'Individual Items'): Observable<any> {
    return this.http.post<any>(`${environment.APIENDPOINT}/v2/jobs/${jobId}/bundles/custom`, { name, items }, {
      headers: this.setHeaders(),
      responseType: 'json'
    });
  }

}

